'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Task {
  id: string;
  title: string;
  assignee: {
    name: string;
    avatar: string;
    alt: string;
  };
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
}

interface FamilyTasksProps {
  tasks: Task[];
}

const FamilyTasks = ({ tasks }: FamilyTasksProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-success';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'in-progress':
        return 'bg-accent/10 text-accent';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (!isHydrated) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 bg-muted rounded-lg animate-pulse"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tasks.map((task) => (
        <div
          key={task.id}
          className="p-4 glass rounded-lg hover:border-primary/30 transition-smooth"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon
                name="FlagIcon"
                size={16}
                className={getPriorityColor(task.priority)}
              />
              <h4 className="text-sm font-caption font-medium text-foreground">
                {task.title}
              </h4>
            </div>
            <span
              className={`px-2 py-1 rounded text-xs font-caption font-medium ${getStatusBadge(
                task.status
              )}`}
            >
              {task.status.replace('-', ' ')}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-muted">
                <AppImage
                  src={task.assignee.avatar}
                  alt={task.assignee.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-xs font-caption text-muted-foreground">
                {task.assignee.name}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs font-caption text-muted-foreground">
              <Icon name="CalendarIcon" size={14} />
              <span>{task.dueDate}</span>
            </div>
          </div>
        </div>
      ))}
      <Link
        href="/family-coordination-hub"
        className="block w-full p-3 text-center text-sm font-caption font-medium text-primary hover:bg-primary/5 rounded-lg transition-smooth"
      >
        View All Tasks →
      </Link>
    </div>
  );
};

export default FamilyTasks;